<script>
import { iIcon, iBadge } from '@inscribe/signal';
import palette from '@inscribe/signal/dist/palette';
import {
  COMPLIANCE_RESULT,
  COMPLIANCE_CHECK_TYPE_TO_ICON,
} from '@/constants/businessComplianceAgent';

export default {
  name: 'InformationCardCheckItem',
  components: { iIcon, iBadge },
  props: {
    check: {
      type: Object,
      required: true,
      validator(value) {
        const requiredKeys = ['type', 'title', 'result'];
        return requiredKeys.every((key) => key in value);
      },
    },
    onClick: {
      type: Function,
      required: false,
      default: undefined,
    },
  },
  data() {
    return {
      palette,
      COMPLIANCE_RESULT,
      COMPLIANCE_CHECK_TYPE_TO_ICON,
    };
  },
  computed: {
    isNotApplicable() {
      return this.check.result === COMPLIANCE_RESULT.NOT_APPLICABLE;
    },
  },
  methods: {
    getDescriptionText(checkResult) {
      if (checkResult === COMPLIANCE_RESULT.PASS) {
        return 'Verified';
      } else if (checkResult === COMPLIANCE_RESULT.FAIL) {
        return 'Not in compliance';
      } else if (checkResult === COMPLIANCE_RESULT.NOT_APPLICABLE) {
        return this.check.rationale || 'Not applicable';
      } else {
        return 'Requires manual review';
      }
    },
    handleClick() {
      if (!this.isNotApplicable && this.onClick) {
        this.onClick(this.check);
      }
    },
  },
};
</script>

<template>
  <button
    class="i-py-6 i-px-8 d-flex flex-row w-100 align-items-center i-gap-7"
    @click="handleClick"
    :class="{ 'not-clickable': isNotApplicable }"
  >
    <i-icon
      :symbol="COMPLIANCE_CHECK_TYPE_TO_ICON[check.type]"
      :size="22"
      :colour="palette['light-neutral-110']"
    ></i-icon>

    <div class="d-flex w-100 flex-column">
      <span class="i-text-subheading-2 i-text-neutral-120">{{
        check.title
      }}</span>
      <span class="i-text-body-3 i-text-neutral-90">{{
        getDescriptionText(check.result)
      }}</span>
    </div>

    <div class="check-result-icon">
      <i-badge
        v-if="check.result === COMPLIANCE_RESULT.PASS"
        icon="small-solid-check"
        colour="success"
        has-leading-icon
        label="Pass"
      />
      <i-badge
        v-else-if="check.result === COMPLIANCE_RESULT.FAIL"
        icon="small-solid-delete"
        colour="error"
        has-leading-icon
        label="Fail"
      />
      <i-badge
        v-else-if="check.result === COMPLIANCE_RESULT.NOT_APPLICABLE"
        icon="small-solid-minus"
        colour="neutral"
        has-leading-icon
        label="N/A"
      />
      <i-badge
        v-else
        icon="small-solid-alert-circle"
        colour="warning"
        has-leading-icon
        label="Inconclusive"
      />
    </div>

    <i-icon
      symbol="small-chevron-right"
      :size="18"
      :colour="
        isNotApplicable
          ? palette['light-neutral-50']
          : palette['light-neutral-110']
      "
    ></i-icon>
  </button>
</template>

<style lang="scss" scoped>
button {
  // reset default button styles
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;

  &:hover {
    background: $light-neutral-20;
  }
}

.check-result-icon {
  flex-shrink: 0;
}

.not-clickable {
  cursor: default;

  &:hover {
    background: transparent;
  }
}
</style>
